import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Scan } from "./scan";
import {
  Actions,
  Brand,
  Eyebrow,
  GlobalStyle,
  Header,
  Hint,
  Notice,
  PrimaryButton,
  ResultCard,
  ResultLabel,
  ResultValue,
  Root,
  SecondaryButton,
  Stage,
} from "./css/styles";
import { useQueryState } from "./hooks/useQueryState";
import { buildRedirectTarget, isRejectedRedirect } from "./lib/redirect";
import { copyText } from "./lib/clipboard";
import { armAudioUnlock } from "./helper";
import { MediaError } from "./hooks/use-user-media";

const REDIRECT_DELAY_MS = 250;

const CROSSHAIR = { enabled: true, style: "square" };

/** Camera failures the user can only escape by leaving the in-app browser. */
const BLOCKED_BROWSER_ERRORS = [MediaError.UNSUPPORTED, MediaError.INSECURE];

/** Notifies the embedding app. Contract kept as is for existing integrations. */
function postToParent(barcode) {
  if (window.parent === window) return;
  window.parent.postMessage({ action: "doScanCode", payload: barcode }, "*");
}

export default function App() {
  const [result, setResult] = useState(null);
  const [redirectTarget, setRedirectTarget] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [resumeToken, setResumeToken] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);

  const [redirectParam] = useQueryState("redirect_url");
  const history = useHistory();

  useEffect(armAudioUnlock, []);

  const handleCapture = useCallback(
    (code) => {
      const barcode = code && code.rawcode;
      if (!barcode) return;

      setResult(code);

      const target = buildRedirectTarget(redirectParam, barcode);
      if (!target) {
        postToParent(barcode);
        return;
      }

      // Short delay so the capture tone is audible before the page unloads.
      setRedirectTarget(target);
      setTimeout(() => window.location.replace(target), REDIRECT_DELAY_MS);
    },
    [redirectParam]
  );

  const handleRescan = () => {
    setResult(null);
    setRedirectTarget(null);
    setResumeToken((token) => token + 1);
  };

  const handleBack = () => {
    if (window.history.length > 1) history.goBack();
    else window.close();
  };

  const handleCopyLink = async () => {
    const copied = await copyText(window.location.href);
    setLinkCopied(copied);
  };

  const renderResult = () => {
    if (!result) return null;
    return (
      <ResultCard>
        <ResultLabel>{result.type || "Código lido"}</ResultLabel>
        <ResultValue>{result.rawcode}</ResultValue>
      </ResultCard>
    );
  };

  const renderRedirectFallback = () => {
    if (!redirectTarget) return null;
    // Some in-app browsers drop a navigation started from a timer, leaving the
    // user on a frozen result screen with no way forward.
    return (
      <PrimaryButton as="a" href={redirectTarget}>
        Continuar
      </PrimaryButton>
    );
  };

  const renderBlockedBrowserEscape = () => {
    if (!BLOCKED_BROWSER_ERRORS.includes(cameraError)) return null;
    return (
      <SecondaryButton type="button" onClick={handleCopyLink}>
        {linkCopied ? "Link copiado" : "Copiar link do leitor"}
      </SecondaryButton>
    );
  };

  const renderRejectedRedirectNotice = () => {
    // Stays visible after a capture too: it is the only explanation the user
    // gets for not being handed back to the app that sent them here.
    if (!isRejectedRedirect(redirectParam)) return null;
    return (
      <Notice>
        O endereço de retorno informado não é válido. O código lido será exibido
        nesta tela.
      </Notice>
    );
  };

  return (
    <Fragment>
      <Root>
        <Header>
          <Brand
            src={`${process.env.PUBLIC_URL || ""}/pagfinance_white_2_1_slim.svg`}
            alt="PagFinance"
          />
          <Eyebrow>Leitor de QR Code</Eyebrow>
        </Header>

        <Stage>
          <Scan
            onCapture={handleCapture}
            onCameraError={setCameraError}
            beepOn
            bw={false}
            scanRate={200}
            crosshair={CROSSHAIR}
            resumeToken={resumeToken}
          />

          {!result && !cameraError && (
            <Hint>Centralize o QR Code na área destacada.</Hint>
          )}

          {renderRejectedRedirectNotice()}
          {renderResult()}
        </Stage>

        <Actions>
          {renderRedirectFallback()}
          {result && !redirectTarget && (
            <PrimaryButton type="button" onClick={handleRescan}>
              Escanear novamente
            </PrimaryButton>
          )}
          {renderBlockedBrowserEscape()}
          <SecondaryButton type="button" onClick={handleBack}>
            Voltar
          </SecondaryButton>
        </Actions>
      </Root>
      <GlobalStyle />
    </Fragment>
  );
}
